import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ISectionProps } from "../../../../@types/Patient";
import {
  IPatientProtocolAction,
  IPatientProtocolActionRow,
} from "../../../../@types/Protocol";
import { ChevronDown } from "../../../../assets/icons/ChevronDown";
import { ChevronUp } from "../../../../assets/icons/ChevronUp";
import { CustomIconButton } from "../../../../components/CustomIconButton";
import { usePatientProtocolActions } from "../../../../hooks/queries/protocols";
import useIsMobile from "../../../../hooks/useIsMobile";
import { PageTitle } from "../../../../layout/components/PageTitle";
import { getProtocolActionDateTime } from "../../../../utils/dateFormatter";
import { formatActionFrequency } from "../../../../utils/protocolFrequency";
import { PatientProtocolActionsTable } from "../PatientProtocolActionsTable";
import { TitleContainer } from "./ProtocolActionsSection.styles";

export function ProtocolActionsSection({
  patient,
  updateColapsedSections,
  collapsedSection,
}: ISectionProps) {
  const { t } = useTranslation();

  const { isMobile } = useIsMobile();

  const { protocolActions, isError, isFetching, isRefetching, refetch } =
    usePatientProtocolActions(patient.id);

  const handleRefreshTable = () => {
    refetch();
  };

  // Empty date values are left blank; TableSorted renders "-" for empty cells,
  // matching the rest of the patient page tables.
  const tableData: IPatientProtocolActionRow[] = useMemo(
    () =>
      (protocolActions ?? []).map((row: IPatientProtocolAction) => ({
        id: row.actionId,
        action: row.action ?? "",
        // EN-5283: the API's `frequency` is English, so it is rebuilt from the interval type.
        // The count defaults to 1, as the API does when it formats it.
        frequency: formatActionFrequency(
          t,
          row.intervalType,
          row.intervalIterations > 0 ? row.intervalIterations : 1,
          row.frequency ?? ""
        ),
        startDate: getProtocolActionDateTime(row.startDate),
        lastCompleted: getProtocolActionDateTime(row.lastCompleted),
        nextActionDue: getProtocolActionDateTime(row.nextActionDue),
      })),
    [protocolActions, t]
  );

  const handleCollapseSection = () => {
    updateColapsedSections("protocolActionsSection");
  };

  return (
    <>
      {!isMobile && (
        <TitleContainer>
          <PageTitle>{t("patientPage_sections_protocolActions")}</PageTitle>
          <CustomIconButton
            style={{ marginLeft: "2px" }}
            onClick={handleCollapseSection}
          >
            {collapsedSection.value ? <ChevronUp /> : <ChevronDown />}
          </CustomIconButton>
        </TitleContainer>
      )}
      <div
        style={{
          height: collapsedSection.value ? "0" : "",
          overflow: collapsedSection.value ? "hidden" : "",
        }}
      >
        <PatientProtocolActionsTable
          isLoading={isFetching && !isRefetching}
          isError={isError}
          content={tableData}
          refreshTable={handleRefreshTable}
        />
      </div>
    </>
  );
}
